import { useState, useRef, useEffect, useCallback } from 'react';
import DateSelector from '../components/analytics/DateSelector';
import DeviceFilter from '../components/analytics/DeviceFilter';
import StoreFilter from '../components/analytics/StoreFilter';
import type { AggregateData } from '../types/analyticstypes';
import { defaultDateRange, freshenRangeIfPreset } from '../utils/analytics/dateRange';
import type { DateRange, PresetKey } from '../utils/analytics/dateRange';
import { getAggregate } from '../services/analyticsservice';

const DISPLAY_WIDTH = 800;
const ROUTES = ['All', 'Home', 'Orders', 'CartContent'];

export default function AnalyticsHeatmapPage() {
  const [range, setRange] = useState<DateRange>(() => defaultDateRange());
  const [presetKey, setPresetKey] = useState<PresetKey | null>('today');
  const [storeNumber, setStoreNumber] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [route, setRoute] = useState('All');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AggregateData | null>(null);
  const [radius, setRadius] = useState(40);
  const [intensity, setIntensity] = useState(5);

  const containerRef = useRef<HTMLDivElement>(null);
  const heatmapInstanceRef = useRef<unknown>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const liveRange = freshenRangeIfPreset(range, presetKey);
      if (liveRange !== range) setRange(liveRange);
      const json = await getAggregate({
        from: liveRange.from,
        to: liveRange.to,
        storeNumber: storeNumber || undefined,
        deviceId: deviceId || undefined,
        route: route && route !== 'All' ? route : undefined,
      });
      setData(json);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to fetch aggregate data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, range, presetKey, route, storeNumber]);

  const displayHeight = data
    ? Math.round(DISPLAY_WIDTH * (data.screenHeight / data.screenWidth))
    : Math.round(DISPLAY_WIDTH * (1920 / 1080));

  useEffect(() => {
    if (!data || !containerRef.current) return;

    const scaleX = DISPLAY_WIDTH / data.screenWidth;
    const scaleY = displayHeight / data.screenHeight;

    // heatmap.js-fixed main field is incorrect; import from build dir
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    import('heatmap.js-fixed/build/heatmap.js').then((h337Module: any) => {
      const h337 = h337Module.default || h337Module;

      const container = containerRef.current;
      if (!container) return;
      const existingCanvas = container.querySelector('canvas');
      if (existingCanvas) existingCanvas.remove();
      const existingDiv = container.querySelector('.heatmap-canvas');
      if (existingDiv) existingDiv.remove();

      const heatmapInstance = h337.create({
        container,
        radius,
        maxOpacity: 0.6,
        minOpacity: 0.05,
        blur: 0.75,
      });

      const points = data.touchEvents.map((t) => ({
        x: Math.round(t.x * scaleX),
        y: Math.round(t.y * scaleY),
        value: 1,
      }));

      heatmapInstance.setData({
        max: intensity,
        min: 0,
        data: points,
      });

      heatmapInstanceRef.current = heatmapInstance;
    });
  }, [data, radius, intensity, displayHeight]);

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Touch Heatmap</h2>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <DateSelector
            value={range}
            presetKey={presetKey}
            onChange={(r, k) => {
              setRange(r);
              setPresetKey(k);
            }}
          />
          <StoreFilter value={storeNumber} onChange={setStoreNumber} />
          <DeviceFilter value={deviceId} onChange={setDeviceId} />
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">Screen</label>
            <select
              value={route}
              onChange={(e) => setRoute(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {ROUTES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Loading...' : 'Load'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">{error}</div>
      )}

      {data && (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex justify-center">
              <div
                ref={containerRef}
                className="relative bg-gray-200 border-2 border-gray-300"
                style={{
                  width: DISPLAY_WIDTH,
                  height: displayHeight,
                  backgroundImage:
                    'linear-gradient(rgba(156,163,175,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(156,163,175,0.3) 1px, transparent 1px)',
                  backgroundSize: '50px 50px',
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Controls</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Radius: {radius}</label>
                  <input
                    type="range"
                    min={10}
                    max={80}
                    value={radius}
                    onChange={(e) => setRadius(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Max Intensity: {intensity}</label>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    value={intensity}
                    onChange={(e) => setIntensity(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Total Touches</p>
                  <p className="text-xl font-bold text-gray-900">{data.touchEvents.length.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Sessions</p>
                  <p className="text-xl font-bold text-gray-900">{data.funnel.sessions.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Screen Size</p>
                  <p className="text-xl font-bold text-gray-900">{data.screenWidth}x{data.screenHeight}</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && !data && !error && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg">No data loaded</p>
          <p className="text-sm mt-1">Select a time range and click Load to generate heatmap</p>
        </div>
      )}
    </div>
  );
}
